
import { Component, Vue, Watch, Prop } from "vue-property-decorator";
import ArticleTile from "./ArticleTile.vue";
import MultiEditAssembly from "./MultieditAssembly.vue";
import { CustomerArticleDto } from "@/dtos/customerArticleDto";
import httpClient from "../../store/httpClient";

@Component({
	components: {
		ArticleTile,
		MultiEditAssembly,
	},
})
export default class Assembly extends Vue {
	@Prop()
	private assemblyId!: number;
	private selectedSubarticleIds: number[] = [];
	private articleMultiDeleteDialog: boolean = false;
	private isDeleting: boolean = false;

	private async mounted() {
		this.$store.commit("clearSelectedArticleIds");
		this.$store
			.dispatch("loadMissingArticles", [this.assemblyId])
			.then(() => this.$store.dispatch("loadMissingArticles", this.assembly.subArticleIds));
		// Prevent the articles from being reloaded when returning to the article dashboard.
		this.$store.commit("setReloadArticles", false);
	}

	/**
	 * Gets the article that represents the whole assembly
	 */
	private get assembly(): CustomerArticleDto {
		return this.$store.state.articles.find(
			(a: CustomerArticleDto) => a.articleId == this.assemblyId
		);
	}

	private get subarticles(): CustomerArticleDto[] {
		return this.$store.state.articles.filter(
			(a: CustomerArticleDto) =>
				this.assembly && this.assembly.subArticleIds.includes(a.articleId)
		);
	}

	private get invalidStateArticles(): CustomerArticleDto[] {
		return this.subarticles.filter((a: CustomerArticleDto) => a.locked || a.calculating);
	}
	private get invalidStateArticlesToDelete(): CustomerArticleDto[] {
		return this.invalidStateArticles.filter((a: CustomerArticleDto) =>
			this.selectedSubarticleIds.includes(a.articleId)
		);
	}
	private get validStateArticles(): CustomerArticleDto[] {
		return this.subarticles.filter((a: CustomerArticleDto) => !a.locked && !a.calculating);
	}
	private get validStateArticlesToDelete(): CustomerArticleDto[] {
		return this.validStateArticles.filter((a: CustomerArticleDto) =>
			this.selectedSubarticleIds.includes(a.articleId)
		);
	}

	/**
	 * Disable the "delete" option in the submenu in the article tile, if ...
	 *      - is assembly view mode (always the case in this context)
	 *      - any of the subarticles of this assembly or the assembly itself are calculating
	 */
	private get isDeleteDisabled(): boolean {
		if (!this.assembly) {
			return true;
		}
		const subarticleCalculating = this.subarticles.some(
			(sa: CustomerArticleDto) => sa.calculating
		);
		return this.assembly.calculating || subarticleCalculating;
	}

	private addOrRemoveSelectedSubarticleId(articleId: number) {
		if (this.selectedSubarticleIds.includes(articleId)) {
			this.selectedSubarticleIds.splice(this.selectedSubarticleIds.indexOf(articleId), 1);
		} else {
			this.selectedSubarticleIds.push(articleId);
		}
	}

	private clearSelection() {
		this.selectedSubarticleIds.splice(0, this.selectedSubarticleIds.length);
	}

	private onDeleteSelectedArticles() {
		this.articleMultiDeleteDialog = true;
	}

	private checkEsc(keyPress: KeyboardEvent) {
		if (keyPress.key === "Escape") {
			this.articleMultiDeleteDialog = false;
		}
	}

	private async deleteArticles() {
		try {
			this.isDeleting = true;
			let index = 1;
			const deleteIds = this.validStateArticlesToDelete.map(
				(a: CustomerArticleDto) => a.articleId
			);
			// navigate back to the catalogue if all or all-but-1 subarticles were deleted.
			const numberOfLeftOverSubarticles = this.subarticles.length - deleteIds.length;
			if (numberOfLeftOverSubarticles <= 1) {
				// Wenn nur einer, oder gar keine übrig bleibt, gehen wir wieder auf die Hauptseite.
				// Wir müssen hier schon navigieren, da sich die Anzahl der Subartikel mit SignalR laufend
				// ändert, wenn das Api aufgerufen wurde und ich meine, der Update geht auch nicht sauber.
				this.navigateToCatalogue();
				if (numberOfLeftOverSubarticles === 0) {
					// Baugruppe wird gelöscht, darum müssen wir sie hier auch löschen
					// weil wir das nicht signalisieren können.
					let data = {
						articleId: this.assemblyId,
						refreshCart: false,
					};
					this.$store.dispatch("deleteArticle", data);
				}
			}
			const response = await httpClient().post(
				`article/deleteArticles?force=true`,
				deleteIds
			);
			for (const id of deleteIds) {
				let data = {
					articleId: id,
					refreshCart: index >= deleteIds.length,
				};
				this.$store.dispatch("deleteArticle", data);
				index++;
			}
			this.clearSelection();
		} catch (err) {
			console.log(`Fehler beim Löschen der Artikel. ${err}`);
			this.$store.dispatch(
				"setSnackbarErrorText",
				this.$t("catalogue.assembly.messages.deleteError")
			);
		} finally {
			this.isDeleting = false;
			this.articleMultiDeleteDialog = false;
		}
	}

	private navigateToCatalogue() {
		this.$router.push({ name: "Catalogue" });
	}
}
